import { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import {
  Camera,
  Sparkles,
  AlertTriangle,
  ShieldCheck,
  X,
  Loader2,
  ArrowUpRight,
  ChevronRight,
  Image as ImageIcon,
  Trash2,
  Info,
  CheckCircle2,
  Circle,
} from "lucide-react";

import Layout from "../components/Layout";
import AiOutputDisclaimer from "../components/AiOutputDisclaimer";
import { api, API } from "../lib/api";
import { errMsg } from "../lib/errors";
import { yieldToBrowser } from "../lib/upload";
import { readTopicFromSearch, topicLabel, clearPersistedTopic } from "../lib/qaTopic";
import {
  startChairsideTimer,
  rememberLastCase,
  stopChairsideTimer,
} from "../lib/chairsideStore";
import { emit, EVENTS } from "../lib/analytics";
import ChairsideTimer from "../components/ChairsideTimer";

/* -------------------------------------------------------------------------- */
/*  Quick Assessment — Tier 1 Case Intake (v3, 2026-02)                       */
/*                                                                            */
/*  Architectural contract:                                                   */
/*  - Images are uploaded and stored for the Specialist Review workflow only. */
/*  - Image pixels NEVER reach the LLM (see backend/quick_assessment.py).     */
/*  - The AI works from clinician-entered TEXT metadata only.                 */
/* -------------------------------------------------------------------------- */

const MAX_PHOTOS = 8;

const IMAGE_KINDS = [
  "FMX",
  "panoramic",
  "periapical",
  "bitewing",
  "CBCT slice",
  "clinical photo",
  "other",
];

/* -------------------------------------------------------------------------- */
/*  Persistent privacy banner (required on every Tier-1 surface)              */
/* -------------------------------------------------------------------------- */
function PrivacyBanner() {
  return (
    <div
      data-testid="qa-privacy-notice"
      className="mt-4 p-4 rounded-lg bg-amber-500/10 border border-amber-500/40 flex items-start gap-3"
    >
      <Info size={16} className="text-amber-300 mt-0.5 shrink-0" />
      <div className="text-xs text-amber-100 leading-relaxed">
        <strong className="text-amber-200">Preview environment.</strong> Upload synthetic
        or self-de-identified images only. <strong>ProsthoAI does not currently remove
        DICOM metadata.</strong> A separate local-first privacy module is planned.
      </div>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/*  Red-flag modal + Complexity badge — retained for v1-LEGACY docs only.     */
/* -------------------------------------------------------------------------- */
function ComplexityBadge({ value }) {
  const map = {
    simple: { bg: "bg-emerald-500/10", border: "border-emerald-500/50", text: "text-emerald-300" },
    moderate: { bg: "bg-amber-500/10", border: "border-amber-500/50", text: "text-amber-300" },
    complex: { bg: "bg-red-500/10", border: "border-red-500/60", text: "text-red-300" },
  };
  const s = map[value] || map.moderate;
  return (
    <span
      data-testid={`qa-complexity-${value}`}
      className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10px] uppercase tracking-wider border ${s.bg} ${s.border} ${s.text}`}
    >
      {value}
    </span>
  );
}

function RedFlagModal({ open, redFlags, onAcknowledge, busy }) {
  if (!open) return null;
  return (
    <div
      data-testid="qa-red-flag-modal"
      className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-red-950/80 backdrop-blur-md"
    >
      <div className="relative w-full max-w-xl bg-[#1a0608] border-2 border-red-500 rounded-2xl shadow-[0_0_120px_-10px_rgba(248,113,113,0.55)] p-8 animate-in fade-in zoom-in-95">
        <div className="flex items-center gap-3 text-red-300">
          <span className="text-3xl" aria-hidden>🔴</span>
          <div className="kicker text-red-300">Historical Alert (Legacy)</div>
        </div>
        <h2 className="font-serif text-3xl text-white mt-3">Red flag identified</h2>
        <p className="mt-4 text-sm text-red-100 leading-relaxed">
          This is a legacy Tier-1 record predating the case-intake reframe. Do not
          proceed without a comprehensive analysis or specialist consultation.
        </p>
        <ul className="mt-5 space-y-2 max-h-48 overflow-y-auto pr-1">
          {redFlags.map((rf, i) => (
            <li
              key={i}
              data-testid={`qa-red-flag-item-${i}`}
              className="text-xs bg-red-500/10 border border-red-500/50 rounded p-3 text-red-100"
            >
              <div className="font-semibold text-red-200">{rf.label}</div>
              <div className="mt-1 leading-relaxed">{rf.detail}</div>
            </li>
          ))}
        </ul>
        <button
          onClick={onAcknowledge}
          disabled={busy}
          data-testid="qa-red-flag-ack"
          className="mt-7 w-full bg-red-500 hover:bg-red-400 text-white font-semibold rounded-md px-6 py-3 transition disabled:opacity-60 flex items-center justify-center gap-2"
        >
          {busy ? <Loader2 size={14} className="animate-spin" /> : <ShieldCheck size={14} />}
          I understand
        </button>
        <p className="mt-3 text-[10px] text-red-300/70 text-center">
          This acknowledgment is permanently logged to the audit trail.
        </p>
      </div>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/*  New: Upload flow                                                          */
/* -------------------------------------------------------------------------- */
export function QuickAssessmentNew() {
  const nav = useNavigate();
  const [params] = useSearchParams();
  const fileInputRef = useRef(null);
  const cameraInputRef = useRef(null);
  // files: [{ id, blob, previewUrl, name, kind, region }]
  const [files, setFiles] = useState([]);
  const topicSlug = readTopicFromSearch(params.toString());
  const preselectedTopicLabel = topicSlug ? topicLabel(topicSlug) : null;
  const [presentingConcern, setPresentingConcern] = useState("");
  const [note, setNote] = useState(
    preselectedTopicLabel ? `Focus area: ${preselectedTopicLabel}. ` : ""
  );
  const [privacyAck, setPrivacyAck] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [uploadPct, setUploadPct] = useState(0);
  const [stage, setStage] = useState(null);

  useEffect(() => {
    if (preselectedTopicLabel) clearPersistedTopic();
  }, [preselectedTopicLabel]);

  // Analytics: case_intake_started + chairside timer starts as soon as this
  // form mounts. Timer is cleared on successful submit.
  const startedAtRef = useRef(Date.now());
  useEffect(() => {
    emit(EVENTS.CASE_INTAKE_STARTED, { feature: "case_intake" });
  }, []);

  const onPick = async (e) => {
    const picked = Array.from(e.target.files || []);
    if (!picked.length) return;
    const kept = picked
      .filter((f) => /^image\/(jpeg|png|heic|heif)/i.test(f.type))
      .slice(0, MAX_PHOTOS - files.length);
    if (kept.length < picked.length) toast("Some files were skipped (must be JPEG/PNG)");
    const placeholders = kept.map((f) => ({
      id: crypto.randomUUID(),
      blob: f,
      previewUrl: URL.createObjectURL(f),
      name: f.name,
      kind: "",
      region: "",
    }));
    setFiles((prev) => [...prev, ...placeholders].slice(0, MAX_PHOTOS));
    e.target.value = "";
    for (const _ph of placeholders) {
      // No AI pre-flight. Deterministic checks live server-side in
      // _assess_usability(). We only yield to keep the UI responsive.
      await yieldToBrowser();
    }
  };

  const removeOne = (id) => setFiles((prev) => prev.filter((f) => f.id !== id));
  const setKind = (id, kind) => setFiles((prev) => prev.map((f) => (f.id === id ? { ...f, kind } : f)));
  const setRegion = (id, region) => setFiles((prev) => prev.map((f) => (f.id === id ? { ...f, region } : f)));

  const allKindsSelected = files.length > 0 && files.every((f) => f.kind);
  const canSubmit = files.length > 0 && allKindsSelected && privacyAck && !submitting;

  useEffect(() => {
    const handler = (e) => {
      const items = e.clipboardData?.items;
      if (!items) return;
      const picked = [];
      for (const it of items) {
        if (it.kind === "file") {
          const f = it.getAsFile();
          if (f && /^image\//i.test(f.type)) picked.push(f);
        }
      }
      if (!picked.length) return;
      e.preventDefault();
      onPick({ target: { files: picked, value: "" } });
      toast.success(`Pasted ${picked.length} image${picked.length === 1 ? "" : "s"}`);
    };
    document.addEventListener("paste", handler);
    return () => document.removeEventListener("paste", handler);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [files.length]);

  const submit = async () => {
    if (!files.length) return toast.error("Add at least one photo");
    if (!allKindsSelected) return toast.error("Select an image type for every photo");
    if (!privacyAck) return toast.error("Please confirm the privacy acknowledgment");
    setSubmitting(true);
    setStage("upload");
    setUploadPct(0);
    const form = new FormData();
    files.forEach((f) => form.append("files", f.blob, f.name));
    form.append("image_kinds", JSON.stringify(files.map((f) => f.kind)));
    form.append("image_regions", JSON.stringify(files.map((f) => f.region || null)));
    form.append("presenting_concern", presentingConcern.trim());
    if (note.trim()) form.append("note", note.trim());
    form.append("privacy_gate_acknowledged", "true");
    try {
      const r = await api.post("/quick-assessment", form, {
        headers: { "Content-Type": "multipart/form-data" },
        onUploadProgress: (evt) => {
          if (!evt.total) return;
          const pct = Math.min(100, Math.round((evt.loaded * 100) / evt.total));
          setUploadPct(pct);
          if (pct >= 100) setStage("analyze");
        },
        timeout: 5 * 60 * 1000,
      });
      toast.success("Case intake prepared");
      const caseId = r.data.id;
      // Chairside chrome: remember the case ID + running timer so mobile
      // navigation to Ask the Bench can offer "Resume last case".
      rememberLastCase(caseId, { status: r.data.review_ready ? "ready" : "pending", created_at: r.data.created_at });
      startChairsideTimer(caseId);
      emit(EVENTS.CASE_INTAKE_COMPLETED, {
        feature: "case_intake",
        duration_ms: Date.now() - startedAtRef.current,
      });
      nav(`/quick-assessment/${caseId}`);
    } catch (err) {
      toast.error(errMsg(err, "Case intake preparation failed"));
    } finally {
      setSubmitting(false);
      setStage(null);
      setUploadPct(0);
    }
  };

  return (
    <Layout>
      <div className="kicker">Tier 1 · Case Intake</div>
      <h1
        data-testid="qa-new-title"
        className="font-serif text-3xl md:text-4xl text-[var(--pa-text)] mt-2"
      >
        Quick Assessment
      </h1>
      <p className="mt-3 text-sm text-[var(--pa-text-muted)] max-w-2xl leading-relaxed">
        Photograph your X-rays or patient from any screen, lightbox, or chairside. ProsthoAI
        organises the images and your notes into a structured case intake for specialist review.
        The AI does not read or interpret the images — that is the specialist's role.
      </p>

      <PrivacyBanner />

      {preselectedTopicLabel && (
        <div
          data-testid="qa-preselected-topic"
          className="mt-5 inline-flex items-center gap-2 rounded-full border border-[var(--pa-gold)] bg-[var(--pa-gold)]/10 px-4 py-2 text-xs uppercase tracking-[0.14em] text-[var(--pa-gold)] font-semibold"
        >
          <span aria-hidden="true">✦</span>
          <span>Focus:&nbsp;</span>
          <span
            data-testid="qa-preselected-topic-label"
            className="normal-case tracking-normal text-[var(--pa-text)]"
          >
            {preselectedTopicLabel}
          </span>
        </div>
      )}

      {/* Capture area */}
      <div
        data-testid="qa-capture-area"
        onDragOver={(e) => e.preventDefault()}
        onDrop={(e) => {
          e.preventDefault();
          onPick({ target: { files: e.dataTransfer.files, value: "" } });
        }}
        className="mt-7 p-6 bg-[var(--pa-surface)] border-2 border-dashed border-[var(--pa-gold)]/40 rounded-lg"
      >
        <div className="flex flex-col md:flex-row md:items-center gap-3">
          <button
            type="button"
            onClick={() => cameraInputRef.current?.click()}
            data-testid="qa-camera-btn"
            className="flex-1 flex items-center justify-center gap-2 bg-[var(--pa-gold)] text-[var(--pa-bg)] font-semibold px-5 py-4 rounded-md hover:bg-[var(--pa-gold-hover)] transition text-base"
          >
            <Camera size={20} /> Open camera
          </button>
          <button
            type="button"
            onClick={() => fileInputRef.current?.click()}
            data-testid="qa-upload-btn"
            className="flex-1 flex items-center justify-center gap-2 bg-[var(--pa-bg-2)] border border-[var(--pa-border)] text-[var(--pa-text)] font-medium px-5 py-4 rounded-md hover:border-[var(--pa-gold)] transition text-base"
          >
            <ImageIcon size={18} /> Upload from library
          </button>
        </div>
        <input
          ref={cameraInputRef}
          type="file"
          accept="image/*"
          capture="environment"
          multiple
          onChange={onPick}
          className="hidden"
          data-testid="qa-camera-input"
        />
        <input
          ref={fileInputRef}
          type="file"
          accept="image/jpeg,image/png,image/heic,image/heif"
          multiple
          onChange={onPick}
          className="hidden"
          data-testid="qa-file-input"
        />
        <p className="mt-4 text-[11px] text-[var(--pa-text-muted)] leading-relaxed">
          Up to {MAX_PHOTOS} photos per assessment. You can also drag &amp; drop or paste
          images (⌘/Ctrl + V).
        </p>
      </div>

      {/* Thumbnails + per-image metadata (clinician-entered kind + region) */}
      {files.length > 0 && (
        <div data-testid="qa-thumbnails" className="mt-5 space-y-3">
          {files.map((f, i) => (
            <div
              key={f.id}
              data-testid={`qa-thumb-row-${i}`}
              className="flex flex-col sm:flex-row gap-3 p-3 rounded-md border border-[var(--pa-border)] bg-[var(--pa-surface)]"
            >
              <div className="relative w-full sm:w-32 h-32 shrink-0 bg-[var(--pa-bg-2)] rounded overflow-hidden">
                <img src={f.previewUrl} alt="" className="w-full h-full object-cover" />
                <button
                  type="button"
                  onClick={() => removeOne(f.id)}
                  data-testid={`qa-thumb-remove-${i}`}
                  aria-label="Remove"
                  className="absolute top-1 right-1 p-1 rounded-full bg-black/60 hover:bg-red-600 text-white transition"
                >
                  <Trash2 size={12} />
                </button>
              </div>
              <div className="flex-1 grid grid-cols-1 sm:grid-cols-2 gap-2">
                <label className="text-xs">
                  <span className="kicker block mb-1">Image type <span className="text-red-400">*</span></span>
                  <select
                    value={f.kind}
                    onChange={(e) => setKind(f.id, e.target.value)}
                    data-testid={`qa-thumb-kind-${i}`}
                    className="w-full bg-[var(--pa-bg-2)] border border-[var(--pa-border)] focus:border-[var(--pa-gold)] focus:outline-none rounded p-2 text-sm text-[var(--pa-text)]"
                  >
                    <option value="">Select…</option>
                    {IMAGE_KINDS.map((k) => <option key={k} value={k}>{k}</option>)}
                  </select>
                </label>
                <label className="text-xs">
                  <span className="kicker block mb-1">Region (optional)</span>
                  <input
                    type="text"
                    value={f.region}
                    onChange={(e) => setRegion(f.id, e.target.value.slice(0, 120))}
                    data-testid={`qa-thumb-region-${i}`}
                    placeholder="e.g. tooth #14, maxillary anterior"
                    className="w-full bg-[var(--pa-bg-2)] border border-[var(--pa-border)] focus:border-[var(--pa-gold)] focus:outline-none rounded p-2 text-sm text-[var(--pa-text)]"
                  />
                </label>
              </div>
            </div>
          ))}
          {!allKindsSelected && (
            <p data-testid="qa-thumb-kind-required" className="text-xs text-amber-300">
              Select an image type for every photo before submitting.
            </p>
          )}
        </div>
      )}

      {/* Presenting concern */}
      <div className="mt-6">
        <label className="kicker block mb-2">Presenting concern (verbatim)</label>
        <textarea
          value={presentingConcern}
          onChange={(e) => setPresentingConcern(e.target.value.slice(0, 2000))}
          data-testid="qa-presenting-concern"
          rows={2}
          placeholder="What is the clinician-reported presenting concern? (no PHI)"
          className="w-full bg-[var(--pa-bg-2)] border border-[var(--pa-border)] focus:border-[var(--pa-gold)] focus:outline-none rounded text-sm text-[var(--pa-text)] p-3"
        />
      </div>

      {/* Optional clinical note */}
      <div className="mt-4">
        <label className="kicker block mb-2">Optional clinical note</label>
        <textarea
          value={note}
          onChange={(e) => setNote(e.target.value.slice(0, 2000))}
          data-testid="qa-note"
          rows={2}
          placeholder="e.g. Cold sensitivity in tooth #19; FMX from prior visit."
          className="w-full bg-[var(--pa-bg-2)] border border-[var(--pa-border)] focus:border-[var(--pa-gold)] focus:outline-none rounded text-sm text-[var(--pa-text)] p-3"
        />
      </div>

      {/* Privacy gate acknowledgment */}
      <div className="mt-6 p-4 rounded-lg border border-[var(--pa-border)] bg-[var(--pa-surface)]">
        <label className="flex items-start gap-3 text-xs text-[var(--pa-text)] leading-relaxed cursor-pointer">
          <input
            type="checkbox"
            checked={privacyAck}
            onChange={(e) => setPrivacyAck(e.target.checked)}
            data-testid="qa-privacy-gate-ack"
            className="mt-0.5 h-4 w-4 accent-[var(--pa-gold)] shrink-0"
          />
          <span>
            I confirm that the images, filenames and typed information contain no
            patient-identifying information or PHI. The files are synthetic or were
            de-identified by me before upload. <strong>ProsthoAI does not currently
            remove DICOM metadata or de-identify text for me.</strong>
          </span>
        </label>
      </div>

      <button
        onClick={submit}
        disabled={!canSubmit}
        data-testid="qa-submit-btn"
        aria-disabled={!canSubmit}
        className="mt-6 w-full md:w-auto flex items-center justify-center gap-2 bg-[var(--pa-gold)] text-[var(--pa-bg)] font-semibold px-7 py-3.5 rounded-md hover:bg-[var(--pa-gold-hover)] transition disabled:opacity-40 disabled:cursor-not-allowed text-base"
      >
        {submitting ? (
          stage === "analyze" ? (
            <><Loader2 size={16} className="animate-spin" /> Preparing case intake…</>
          ) : (
            <><Loader2 size={16} className="animate-spin" /> Uploading {uploadPct}%</>
          )
        ) : (
          <><Sparkles size={16} /> Prepare for specialist review</>
        )}
      </button>

      {submitting && (
        <div
          data-testid="qa-progress"
          className="mt-3 text-xs text-[var(--pa-text-muted)]"
        >
          <div className="h-1 bg-[var(--pa-bg-2)] rounded overflow-hidden">
            <div
              className={`h-full bg-[var(--pa-gold)] transition-all ${
                stage === "analyze" ? "animate-pulse w-full" : ""
              }`}
              style={stage === "analyze" ? undefined : { width: `${uploadPct}%` }}
              data-testid="qa-progress-bar"
            />
          </div>
        </div>
      )}

      <p className="mt-5 text-[11px] text-[var(--pa-text-dim)] max-w-2xl leading-relaxed">
        This is a structured case intake prepared for specialist review — not a diagnostic
        interpretation of the images.
      </p>
    </Layout>
  );
}

/* -------------------------------------------------------------------------- */
/*  Quick Assessment Detail view — v2 case-intake OR v1 legacy placeholder    */
/* -------------------------------------------------------------------------- */
export default function QuickAssessmentDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const [a, setA] = useState(null);
  const [loading, setLoading] = useState(true);
  const [ackBusy, setAckBusy] = useState(false);
  const [reviewBusy, setReviewBusy] = useState(false);
  const [imageBlobs, setImageBlobs] = useState({});

  const load = async () => {
    try {
      const r = await api.get(`/quick-assessment/${id}`);
      setA(r.data);
      // Chairside chrome: remember this case as "last opened" so Ask the
      // Bench can offer to resume it on the next visit.
      rememberLastCase(id, {
        status: r.data.review_ready ? "ready" : "pending",
        created_at: r.data.created_at,
      });
      emit(EVENTS.FEATURE_OPEN, { feature: "case_detail", path: "/quick-assessment/:id" });
    } catch (err) {
      toast.error(errMsg(err, "Could not load assessment"));
    } finally {
      setLoading(false);
    }
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [id]);

  useEffect(() => {
    if (!a?.image_ids?.length) return;
    let cancelled = false;
    (async () => {
      const token = localStorage.getItem("pa_token");
      const next = {};
      for (const fid of a.image_ids) {
        try {
          const res = await fetch(`${API}/files/${fid}/download`, {
            headers: { Authorization: `Bearer ${token}` },
          });
          if (!res.ok) continue;
          const blob = await res.blob();
          next[fid] = URL.createObjectURL(blob);
        } catch {
          /* ignore */
        }
      }
      if (!cancelled) setImageBlobs(next);
    })();
    return () => { cancelled = true; };
  }, [a?.image_ids]);

  const acknowledgeRedFlag = async () => {
    setAckBusy(true);
    try {
      const r = await api.post(`/quick-assessment/${id}/acknowledge-red-flag`);
      setA(r.data);
    } catch (err) {
      toast.error(errMsg(err, "Could not record acknowledgment"));
    } finally {
      setAckBusy(false);
    }
  };

  const requestReview = async () => {
    setReviewBusy(true);
    try {
      const r = await api.post("/reviews/request-tiered", {
        tier_slug: "quick_assessment",
        resource_id: id,
        origin_url: window.location.origin,
      });
      if (r.data.checkout_url) window.location.href = r.data.checkout_url;
      else { toast.success("Specialist review requested"); load(); }
    } catch (err) {
      toast.error(errMsg(err, "Could not request review"));
    } finally {
      setReviewBusy(false);
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center gap-2 text-[var(--pa-text-muted)]">
          <Loader2 size={14} className="animate-spin" /> Loading…
        </div>
      </Layout>
    );
  }
  if (!a) return <Layout>Not found.</Layout>;

  // ─── Legacy v1 placeholder ────────────────────────────────────────────
  if (a.is_legacy) {
    return (
      <Layout>
        <PrivacyBanner />
        <div className="flex items-center gap-2 kicker mt-4">
          <Sparkles size={12} className="text-[var(--pa-gold)]" /> Tier 1 · Historical Record
        </div>
        <h1
          data-testid="qa-detail-title"
          className="font-serif text-3xl md:text-4xl text-[var(--pa-text)] mt-2"
        >
          Historical assessment
        </h1>
        <div className="mt-2 text-xs text-[var(--pa-text-dim)]">
          {new Date(a.created_at).toLocaleString()} · {a.image_count} photo{a.image_count === 1 ? "" : "s"}
        </div>
        <div
          data-testid="qa-legacy-placeholder"
          className="mt-6 p-5 rounded-lg border border-[var(--pa-border)] bg-[var(--pa-surface)]"
        >
          <div className="kicker">Notice</div>
          <p className="mt-2 text-sm text-[var(--pa-text-muted)] leading-relaxed">
            This assessment predates the case-intake reframe. Historical AI output is
            preserved in the database but is not displayed here. Please prepare a new
            case intake or route this record directly to specialist review.
          </p>
        </div>
        <div className="mt-6">
          <Link
            to="/quick-assessment/new"
            data-testid="qa-legacy-new"
            className="inline-flex items-center gap-2 bg-[var(--pa-gold)] text-[var(--pa-bg)] font-semibold px-5 py-2.5 rounded-md hover:bg-[var(--pa-gold-hover)] transition"
          >
            <Sparkles size={14} /> Prepare a new case intake
          </Link>
        </div>
      </Layout>
    );
  }

  // ─── v2 Case-intake view ──────────────────────────────────────────────
  const ci = a.case_intake || {};
  const inventory = ci.image_inventory || [];
  const missing = a.missing_information || [];
  const questions = a.clarifying_questions || [];
  const checklist = a.specialist_evaluation_checklist || [];

  return (
    <Layout>
      <div className="flex items-center gap-2 kicker">
        <Sparkles size={12} className="text-[var(--pa-gold)]" /> Tier 1 · Case Intake
      </div>
      <h1
        data-testid="qa-detail-title"
        className="font-serif text-3xl md:text-4xl text-[var(--pa-text)] mt-2"
      >
        Case intake summary
      </h1>
      <div className="mt-2 flex flex-wrap items-center gap-2 text-xs text-[var(--pa-text-dim)]">
        <span>{new Date(a.created_at).toLocaleString()}</span>
        <span>·</span>
        <span>{a.image_count} photo{a.image_count === 1 ? "" : "s"}</span>
        <span>·</span>
        <ChairsideTimer caseId={id} />
        {a.review_ready ? (
          <span
            data-testid="qa-review-ready-yes"
            className="inline-flex items-center gap-1 text-emerald-300"
          >
            <CheckCircle2 size={12} /> Review ready
          </span>
        ) : (
          <span
            data-testid="qa-review-ready-no"
            className="inline-flex items-center gap-1 text-amber-300"
          >
            <Circle size={12} /> Not yet review ready
          </span>
        )}
      </div>

      <AiOutputDisclaimer className="mt-4" />
      <PrivacyBanner />

      {/* Image grid — displayed for the clinician; the AI never saw these. */}
      {a.image_ids?.length > 0 && (
        <div
          data-testid="qa-image-grid"
          className="mt-6 grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-3"
        >
          {a.image_ids.map((fid, i) => (
            <div
              key={fid}
              className="aspect-square rounded-md overflow-hidden border border-[var(--pa-border)] bg-[var(--pa-bg-2)]"
              data-testid={`qa-image-${i}`}
            >
              {imageBlobs[fid] ? (
                <img src={imageBlobs[fid]} alt="" className="w-full h-full object-cover" />
              ) : (
                <div className="w-full h-full flex items-center justify-center text-[var(--pa-text-dim)]">
                  <Loader2 size={14} className="animate-spin" />
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Image inventory (clinician-entered kind + region) */}
      {inventory.length > 0 && (
        <section className="mt-8" data-testid="qa-image-inventory">
          <div className="kicker">Image Inventory (clinician-entered)</div>
          <ul className="mt-3 space-y-2">
            {inventory.map((row, i) => (
              <li
                key={i}
                className="text-sm text-[var(--pa-text)] flex flex-wrap items-center gap-2 p-3 rounded-md border border-[var(--pa-border)] bg-[var(--pa-surface)]"
              >
                <span className="text-[var(--pa-gold)] font-semibold">
                  Image {row.image_ref}
                </span>
                <span>·</span>
                <span>{row.kind_clinician}</span>
                {row.region_clinician && (<><span>·</span><span>{row.region_clinician}</span></>)}
                <span>·</span>
                <span className="text-xs text-[var(--pa-text-dim)]">
                  file: {row.usability}
                </span>
              </li>
            ))}
          </ul>
        </section>
      )}

      {/* Presenting concern + clinician note */}
      {(ci.clinician_presenting_concern || ci.clinician_note) && (
        <section className="mt-8" data-testid="qa-clinician-echo">
          <div className="kicker">Clinician-Entered Context</div>
          {ci.clinician_presenting_concern && (
            <div className="mt-2">
              <div className="text-[10px] uppercase tracking-widest text-[var(--pa-text-dim)] mb-1">
                Presenting concern
              </div>
              <p className="text-sm text-[var(--pa-text)] whitespace-pre-wrap">
                {ci.clinician_presenting_concern}
              </p>
            </div>
          )}
          {ci.clinician_note && (
            <div className="mt-3">
              <div className="text-[10px] uppercase tracking-widest text-[var(--pa-text-dim)] mb-1">
                Clinical note
              </div>
              <p className="text-sm text-[var(--pa-text)] whitespace-pre-wrap">
                {ci.clinician_note}
              </p>
            </div>
          )}
        </section>
      )}

      {/* Clarifying questions */}
      {questions.length > 0 && (
        <section className="mt-8" data-testid="qa-clarifying-questions">
          <div className="kicker">Clarifying Questions</div>
          <ul className="mt-3 space-y-2">
            {questions.map((cq, i) => (
              <li
                key={i}
                className="text-sm text-[var(--pa-text)] p-3 rounded-md border border-[var(--pa-border)] bg-[var(--pa-surface)]"
              >
                <div className="font-semibold">{cq.q}</div>
                {cq.why && (
                  <div className="mt-1 text-xs text-[var(--pa-text-muted)]">{cq.why}</div>
                )}
              </li>
            ))}
          </ul>
        </section>
      )}

      {/* Specialist evaluation checklist */}
      {checklist.length > 0 && (
        <section className="mt-8" data-testid="qa-checklist">
          <div className="kicker">Specialist Evaluation Checklist</div>
          <ul className="mt-3 space-y-2">
            {checklist.map((item, i) => (
              <li
                key={i}
                className="text-sm text-[var(--pa-text)] flex gap-3 p-3 rounded-md border border-[var(--pa-border)] bg-[var(--pa-surface)]"
              >
                <ChevronRight size={14} className="text-[var(--pa-gold)] mt-0.5 shrink-0" />
                <span>{item}</span>
              </li>
            ))}
          </ul>
        </section>
      )}

      {/* Missing information */}
      {missing.length > 0 && (
        <section className="mt-8" data-testid="qa-missing">
          <div className="kicker">Missing Information</div>
          <ul className="mt-3 space-y-2">
            {missing.map((m, i) => (
              <li
                key={i}
                className="text-sm text-amber-200 flex gap-2 p-3 rounded-md border border-amber-500/30 bg-amber-500/5"
              >
                <AlertTriangle size={13} className="text-amber-300 mt-0.5 shrink-0" />
                <span>{m}</span>
              </li>
            ))}
          </ul>
        </section>
      )}

      {/* Routing note */}
      {a.routing_note && (
        <section className="mt-8" data-testid="qa-routing-note">
          <div className="kicker">Routing Note</div>
          <p className="mt-2 text-sm text-[var(--pa-text-muted)] whitespace-pre-wrap">
            {a.routing_note}
          </p>
        </section>
      )}

      {/* CTA row */}
      <div className="mt-10 grid md:grid-cols-2 gap-4">
        <button
          type="button"
          onClick={requestReview}
          disabled={reviewBusy}
          data-testid="qa-route-to-specialist"
          className="block p-6 rounded-lg bg-gradient-to-br from-[var(--pa-surface)] to-[var(--pa-bg-2)] border-2 border-[var(--pa-gold)] hover:shadow-[0_0_60px_-10px_rgba(212,175,55,0.45)] transition text-left disabled:opacity-60"
        >
          <ShieldCheck size={22} className="text-[var(--pa-gold)]" />
          <div className="font-serif text-xl text-[var(--pa-text)] mt-3">
            Route to Specialist Review
          </div>
          <p className="text-xs text-[var(--pa-text-muted)] mt-2">
            Send this case intake plus the stored images to a human specialist for review.
          </p>
          <div className="mt-3 inline-flex items-center gap-1 text-[var(--pa-gold)] text-sm font-semibold">
            {reviewBusy ? <><Loader2 size={12} className="animate-spin" /> Requesting…</> : <>Request review <ArrowUpRight size={14} /></>}
          </div>
        </button>
        <Link
          to="/case-analysis/new"
          data-testid="qa-upgrade-cca"
          className="block p-6 rounded-lg bg-[var(--pa-surface)] border border-[var(--pa-border)] hover:border-[var(--pa-gold)] transition"
        >
          <Sparkles size={22} className="text-[var(--pa-text-muted)]" />
          <div className="font-serif text-xl text-[var(--pa-text)] mt-3">
            Upgrade to Complete Case Analysis
          </div>
          <p className="text-xs text-[var(--pa-text-muted)] mt-2">
            Upload full clinical data (DICOM, models, records) for comprehensive planning.
          </p>
          <div className="mt-3 inline-flex items-center gap-1 text-[var(--pa-gold)] text-sm font-semibold">
            Start CCA <ArrowUpRight size={14} />
          </div>
        </Link>
      </div>

      <p className="mt-8 text-[11px] text-[var(--pa-text-dim)] max-w-2xl leading-relaxed">
        This is a structured case intake prepared for specialist review — not a diagnostic
        interpretation of the images. For comprehensive prosthodontic treatment planning,
        proceed to Complete Case Analysis.
      </p>

      {/* Retained for v1-legacy code paths only; new v2 docs never trigger this modal. */}
      <RedFlagModal open={false} redFlags={[]} onAcknowledge={() => {}} busy={false} />
    </Layout>
  );
}

// Re-export utility bits some other pages import.
export { ComplexityBadge };
